import { useNavigate } from "react-router-dom";
import { useMemo } from "react";
import { useAuth } from "../../context/AuthContext";
import { useSavings, useTarget } from "../../hooks/useSavings";
import { useLoans, useSuretyRequests } from "../../hooks/useLoans";
import Card from "../../components/ui/Card";
import StatCard from "../../components/ui/StatCard";
import Badge from "../../components/ui/Badge";
import Button from "../../components/ui/Button";
import ProgressRing from "../../components/charts/ProgressRing";

const fmt = (n: number) =>
new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN", currencyDisplay: "symbol", maximumFractionDigits: 0 }).format(n);
const fmtDate = (d: string) =>
  d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

export default function DashboardPage() {
  const { user }                         = useAuth();
  const navigate                         = useNavigate();
  const { data: savingsData }            = useSavings()         as any;
  const { data: loans = [] }             = useLoans()           as any;
  const { data: suretyReqs = [] }        = useSuretyRequests()  as any;

  const {account = [], contributions={},schedule={}} = savingsData || {};
  const { data: targetValues, isPulling } = useTarget(account?.[0]?.member_id || "") as any;
  const charges   = Number(account?.[0]?.charges ?? 0);
  const balance   = ((account?.[0]?.balance ?? 0) - charges)<0? 0 : Number((account?.[0]?.balance ?? 0) - charges);
  const locked    = Number(account?.[0]?.locked_amount ?? 0);  
  const activeLoan = (loans as any[]).find((l: any) => l.status === "active" || l.status === "disbursed");
  const pendingSurety = (suretyReqs as any[]).filter((r: any) => r.status === "pending").length;
    
  const account_savings = account?.[0]?.balance || 0;
  const account_investment = account?.[1]?.balance || 0;
  const account_targeted = account?.[2]?.balance || 0;
  const portfolio = Number(account_savings) + Number(account_investment) + Number(account_targeted);
  const account_contribution = (portfolio - charges) < 0? 0 : (portfolio - charges);

  const GOAL = targetValues?.[0]?.target_amount ?? 1000000;
  const pct  = Math.min(100, Math.round((account_investment / GOAL) * 100));

  const today = new Date();
  const dateJoined = new Date(user?.created_at.toString() || "");
  const dateDiff = today.getTime() - dateJoined.getTime();
  const sixMonths = 182.5 * 24 * 60 * 60 * 1000;
  
  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  }, []);

  return (
    <div>
      <div className="flex justify-between items-start mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-medium text-slate-800">
            {greeting}, {user?.first_name} 👋 
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">Here's your financial overview</p>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost"   size="sm" onClick={() => navigate("/savings")}>+ Contribute / Repay</Button>
          {user?.kyc_status != "verified"? "" : dateDiff >= sixMonths?
          <Button size="sm" onClick={() => navigate("/loans/apply")}>Apply for Loan</Button> : ""
          }
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <StatCard label="Portfolio Value (Minus charges)"      value={fmt(account_contribution)}       sub={`One-Off Fees Charged: ${fmt(charges)}`}       color="text-emerald-600" icon="💰"/>
        <StatCard label="Locked as Surety" value={fmt(locked)}        sub="Cannot withdraw"             color="text-amber-600"  icon="🔒"/>
        <StatCard label="Active Loans"     value={loans.filter((l:any)=>l.status==="active" || l.status==="disbursed").length} sub={activeLoan ? fmt(activeLoan.total_amount) : "None"} color="text-blue-600" icon="🤝"/>
        <StatCard label="Surety Requests"  value={pendingSurety}       sub="Awaiting sign-off"           color="text-slate-600"  icon="✍️"/>
      </div>

      {/* Surety alert banner */}
      {pendingSurety > 0 && (
        <Card className="mb-5 border-amber-400 bg-amber-50">
          <div className="flex justify-between items-center">
            <p className="text-sm font-medium text-amber-800">
              ⚠️ You have {pendingSurety} surety request{pendingSurety > 1 ? "s" : ""} awaiting your sign-off
            </p>
            <Button variant="amber" size="sm" onClick={() => navigate("/loans")}>Review now</Button>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
        {/* Savings ring */}
        <Card>
          <p className="text-sm font-medium text-slate-700 mb-4">Savings goal progress and account breakdown</p>
          <div className="flex items-center gap-6">
            <div className="relative flex-shrink-0">
              <ProgressRing pct={pct} size={90} stroke={6} color={pct<0? "#d97706" : "#10b981"}/>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <p className="text-l font-medium text-emerald-700">{pct}%</p>
                <p className="text-[10px] text-slate-400">of goal</p>
              </div>
            </div>
            <div>
              {/* <p className="text-xs text-slate-500 mb-1">Saved so far</p>
              <p className="text-xl font-medium text-emerald-600 mb-2">{fmt(balance)}</p>
              <p className="text-xs text-slate-400">Cadence: {savingsData?.schedule?.cadence ?? "monthly"}</p>
              <p className="text-xs text-slate-400 mt-0.5">
                Next: {savingsData?.schedule?.next_run_at ? fmtDate(savingsData.schedule.next_run_at) : "Jun 1, 2025"}
              </p> */}
            </div>

            <div>
              <p className="text-xs text-slate-500 mb-1">Savings</p>
              <p className="text-l md:text-2xl font-medium mt-1 mb-1.5 md:mt-1.5 md:mb-0.5 text-emerald-600">{fmt(account_savings)}</p>
              <p className="text-xs text-slate-500 mb-1">Targeted Savings Account</p>
              <p className="text-l md:text-2xl font-medium mt-1 mb-1.5 md:mt-1.5 md:mb-0.5 text-emerald-600">{fmt(account_targeted)}</p> 
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">Investment Account</p>
              <p className="text-l md:text-2xl font-medium mt-1 mb-1.5 md:mt-1.5 md:mb-0.5 text-emerald-600">{fmt(account_investment)}</p>
              <p className="text-xs text-slate-500 mb-1">One-time charge</p>
              <p className="text-l md:text-2xl font-medium mt-1 mb-1.5 md:mt-1.5 md:mb-0.5 text-red-500">{fmt(charges)}</p>
            </div>            
          </div>
          <div className="mt-4 h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500 rounded-full transition-all duration-500" style={{ width: `${pct}%` }}/>
          </div>
        </Card>

        {/* Active loan */}
        <Card>
          <p className="text-sm font-medium text-slate-700 mb-4">Active loan</p>
          {activeLoan ? (() => {
            const repaidPct = Math.round(((activeLoan.amount_repaid ?? 0) / activeLoan.total_amount) * 100);
            return (
              <div>
                <div className="flex justify-between items-start mb-1">
                  <p className="text-2xl font-medium text-slate-800">{fmt(activeLoan.total_amount)}</p>
                  <Badge label="Active" color="emerald"/>
                </div>
                <p className="text-xs text-slate-400 mb-3">{activeLoan.purpose}</p>
                <div className="flex justify-between text-xs text-slate-500 mb-1.5">
                  <span>Repaid: {fmt(activeLoan.amount_repaid ?? 0)}</span>
                  <span>{repaidPct}%</span>
                </div>
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mb-3">
                  <div className="h-full bg-blue-500 rounded-full" style={{ width: `${repaidPct}%` }}/>
                </div>
                <p className="text-xs text-slate-400">Due: {fmtDate(activeLoan.due_at)} · Sureties: {activeLoan.sureties?.length ?? 0}</p>
              </div>
            );
          })() : (
            <div className="text-center py-4">
              <span className="text-3xl">✨</span>
              <p className="text-sm text-slate-500 mt-2">No active loans</p>
              {user?.kyc_status != "verified"? "" : dateDiff >= sixMonths?
              <Button size="sm" className="mt-3" onClick={() => navigate("/loans/apply")}>Apply now</Button> : ""
              }
            </div>
          )}
        </Card>
      </div>
      
      {/* Recent activity */}
      <Card>
        <div className="flex justify-between items-center mb-4">
          <p className="text-sm font-medium text-slate-700">Recent activity</p>
          <Button variant="ghost" size="sm" onClick={() => navigate("/activity")}>View all</Button>
        </div>
        {(savingsData?.contributions ?? []).slice(0, 4).map((c: any) => (
          <div key={c.id} className="flex justify-between items-center py-2.5 border-b border-slate-100 last:border-0">
            <div className="flex gap-3 items-center">
              <span className="text-lg">{c.type === "contribution" ? "💰" : c.type === "withdrawal" ? "📤" : c.type === "investment" ? "💼" :  c.type === "loan-repayment" ? "🔄" : "💰"}</span>
              <div>
                <p className="text-sm font-medium text-slate-700">
                  {c.type === "contribution" ? "Contribution" : c.type === "withdrawal" ? "Withdrawal" : c.type === "savings" ? "Savings" : c.type === "investment" ? "Investment" : c.type === "targeted" ? "Targeted Savings" : "Loan repayment"}
                </p>
                <p className="text-xs text-slate-400">{fmtDate(c.created_at)}</p>
              </div>
            </div>
            <p className={`text-sm font-medium ${c.type === "withdrawal" ? "text-red-500" : "text-emerald-600"}`}>
              {c.type === "withdrawal" ? "-" : "+"}{fmt(c.amount)}
            </p>
          </div>
        ))}
        {(!savingsData?.contributions || savingsData.contributions.length === 0) && (
          <p className="text-sm text-slate-400 text-center py-4">No transactions yet</p>
        )}
      </Card>
    </div>
  );
}
